import * as crypto from 'crypto';
import base64url from 'base64url';
import Dictionaries from './dictionaries.service';
import { Injectable } from '@angular/core';

interface AllowCredential {
  type: string;
  id: string | Buffer;
  transports: string[];
}
export interface AssertionChallenge {
  allowCredentials: AllowCredential[];
  userVerification: string;
  status: string;
  challenge: string | Buffer | undefined;
  session?: {
    challenge: string | Buffer | undefined;
    name: string;
    loggedIn?: boolean;
  };
}

@Injectable({
  providedIn: 'root',
})
export default class AssertionChallengeBuilder {
  private readonly result: { allowCredentials: AllowCredential[]; userVerification: string } = {
    allowCredentials: [],
    userVerification: '',
  };
  constructor(private readonly dictionaries: Dictionaries) {}

  public addAllowedCredential(id: string): this {
    const { PublicKeyCredentialType, AuthenticatorTransport } = this.dictionaries;
    let { allowCredentials = [] } = this.result ?? {};

    if (!Array.isArray(allowCredentials)) {
      allowCredentials = [allowCredentials];
    }

    const type = PublicKeyCredentialType.PUBLIC_KEY;
    const transports = Object.values(AuthenticatorTransport);

    if (!id || !Object.values(PublicKeyCredentialType).includes(type)) {
      throw new Error('Invalid PublicKeyCredentialDescriptor.');
    }

    allowCredentials.push({ type, id, transports });
    this.result.allowCredentials = allowCredentials;

    return this;
  }

  public setUserVerification(userVerification = this.dictionaries.UserVerificationRequirement.PREFERRED): this {
    const values = Object.values(this.dictionaries.UserVerificationRequirement);

    if (!values.includes(userVerification)) {
      throw new Error(`Invalid UserVerificationRequirement value. Must be one of "${values.join('", "')}".`);
    }

    this.result.userVerification = userVerification;

    return this;
  }

  public build(override: { status: string }): AssertionChallenge {
    const challenge = base64url.encode(crypto.randomBytes(32).toString('hex'));
    const { allowCredentials } = this.result;

    if (!allowCredentials) {
      throw new Error('Requires at least one allowed credential.');
    }

    return { ...this.result, ...override, challenge };
  }
}
